"use client";

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface BFLModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  buttonText?: string;
  subtitle?: string;
  modalData?: string;
  children: ReactNode;
  onClose: () => void;
  enableScroll?: boolean;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  onPrimaryButtonClick?: () => void;
  showHeader?: boolean;
  contentClassName?: string;
  modalClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

const BFLModal: FC<BFLModalProps> = ({
  isOpen,
  title,
  subtitle,
  description,
  buttonText = "",
  children,
  onClose,
  enableScroll = true,
  secondaryButtonText,
  onSecondaryButtonClick,
  showHeader,
  onPrimaryButtonClick,
  contentClassName,
  modalClassName,
  bodyClassName,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const el = contentRef.current;

    const handleScroll = () => {
      if (!el) return;
      setIsScrolled(el.scrollTop > 0);
    };

    if (el && enableScroll) {
      el.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    return () => {
      if (el && enableScroll) {
        el.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpen, enableScroll]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <button
        className="bg-black-500 bg-opacity-50 fixed inset-0 z-40"
        onClick={onClose}
        aria-label="Close modal"
      />

      <div
        className="fixed inset-0 z-55 flex items-end justify-center md:items-center lg:px-4"
        onClick={onClose}
      >
        <div
          className={`relative w-full rounded-lg bg-white lg:max-w-[512px] ${modalClassName || ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader !== false && (
            <div className={`px-4 pt-3 pb-4 ${isScrolled ? "shadow-[0_5px_10px_#0003]" : ""}`}>
              <button
                onClick={onClose}
                className="text-link-primary absolute top-[12px] right-[10px] cursor-pointer text-xs"
              >
                <i className="icomoon before:content-['\e914']"></i>
              </button>
              {title && (
                <h2 className="mt-4 text-center text-lg leading-[21.33px] font-medium">{title}</h2>
              )}
              {subtitle && <p className="mt-1 text-center text-xs text-gray-900">{subtitle}</p>}
              {description && <p className="mt-2 text-center text-xs">{description}</p>}
            </div>
          )}

          <div
            ref={contentRef}
            className={`px-4 text-xs md:text-sm ${
              enableScroll
                ? `custom-scroll overflow-auto ${contentClassName ?? "max-h-[306px]"}`
                : ""
            } ${bodyClassName || ""}`}
          >
            {children}
          </div>

          <div
            className={`w-full px-4 pb-4 ${
              isScrolled ? "shadow-[-5px_-5px_10px_#0003]" : ""
            } flex flex-row justify-center gap-3 sm:gap-4`}
          >
            {secondaryButtonText && onSecondaryButtonClick ? (
              <>
                <button
                  onClick={onSecondaryButtonClick}
                  className="mt-4 min-w-0 flex-1 cursor-pointer rounded-[200px] border border-orange-500 px-4 py-[11px] text-sm leading-[18px] font-medium text-orange-500 uppercase transition-all duration-300 ease-in-out hover:bg-orange-50 sm:flex-none sm:basis-[200px]"
                >
                  {secondaryButtonText}
                </button>
                {buttonText && (
                  <button
                    onClick={onPrimaryButtonClick}
                    className="mt-4 min-w-0 flex-1 cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-4 py-[11px] text-sm leading-[18px] font-medium text-white uppercase transition-all duration-300 ease-in-out hover:bg-gradient-to-l hover:from-[#ff6700] hover:to-[#ff8900] sm:flex-none sm:basis-[200px]"
                  >
                    {buttonText}
                  </button>
                )}
              </>
            ) : (
              buttonText && (
                <button
                  onClick={onClose}
                  className="mt-4 min-w-0 flex-1 cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-4 py-[11px] text-sm leading-[18px] font-medium text-white uppercase transition-all duration-300 ease-in-out hover:bg-gradient-to-l hover:from-[#ff6700] hover:to-[#ff8900] sm:flex-none sm:basis-[200px]"
                >
                  {buttonText}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default BFLModal;
